<template>
  <div id="app" :class="{ 'hide-menu': !isMenuVisible || !user }">
    <Header
      title="ML 18 TECNOLOGIA LTDA"
      :hideToggle="!user"
      :hideUserDropdown="!user"
    />
    <Menu v-if="user" />
    <Loading v-if="validatingToken" />
    <Content v-else />
    <Footer />

    <!-- Componente de Notificação Global -->
    <!-- O 'ref="toast"' permite acessar os métodos do componente diretamente -->
    <ToastNotification
      :message="notification.message"
      :type="notification.type"
      :duration="notification.duration"
      ref="toast"
    />
  </div>
</template>

<script>
import axios from "axios";
import { baseApiUrl, userKey } from "@/global";
import { mapState } from "vuex";
import Header from "@/components/template/Header";
import Menu from "@/components/template/Menu";
import Content from "@/components/template/Content";
import Footer from "@/components/template/Footer";
import Loading from "@/components/template/Loading";
import Privacidade from "@/components/home/Privacidade";

// Importe o cliente Socket.IO
import io from "socket.io-client";

// Importe o componente de notificação
import ToastNotification from "@/components/template/ToastNotification";

export default {
  name: "App",
  components: {
    Header,
    Menu,
    Content,
    Footer,
    Loading,
    Privacidade,
    ToastNotification,
  },
  computed: mapState(["isMenuVisible", "user"]),
  data: function () {
    return {
      validatingToken: true,
      socket: null, // Variável para armazenar a instância da conexão Socket.IO
      notification: {
        // Objeto para gerenciar o estado da notificação a ser exibida
        message: "",
        type: "info", // Tipo padrão: 'info', pode ser 'success', 'error', 'warning'
        duration: 3000, // Duração padrão em milissegundos (3 segundos)
      },
    };
  },
  methods: {
    async validateToken() {
      this.validatingToken = true;

      const json = localStorage.getItem(userKey);
      const userData = JSON.parse(json);
      this.$store.commit("setUser", null);

      // Se não houver dados de usuário, redireciona para a tela de autenticação
      if (!userData) {
        this.validatingToken = false;
        this.$router.push({ name: "auth" });
        return;
      }

      try {
        // Tenta validar o token do usuário com o backend
        const res = await axios.post(`${baseApiUrl}/validateToken`, userData);

        if (res.data) {
          // Se o token for válido, define o usuário no Vuex
          this.$store.commit("setUser", userData);

          // Ajusta a visibilidade do menu para dispositivos móveis
          if (this.$mq === "xs" || this.$mq === "sm") {
            this.$store.commit("toggleMenu", false);
          }

          // Após o login bem-sucedido, inicializa a conexão Socket.IO
          this.initSocketIO();
        } else {
          // Se o token não for válido, remove os dados do usuário e redireciona
          localStorage.removeItem(userKey);
          this.$router.push({ name: "auth" });
        }
      } catch (error) {
        // Em caso de erro na requisição de validação do token
        console.error("Erro ao validar token:", error);
        localStorage.removeItem(userKey);
        this.$router.push({ name: "auth" });
      } finally {
        this.validatingToken = false;
      }
    },
    // Método para inicializar a conexão Socket.IO
    initSocketIO() {
      // Conecta-se ao Socket.IO apenas se o usuário estiver logado e a conexão ainda não existir
      if (this.user && !this.socket) {
        // Usando process.env.VUE_APP_API_URL_SOCKET como preferência,
        // ou baseApiUrl como fallback (que você já definiu como o IP do backend)
        const socketUrl = process.env.VUE_APP_API_URL_SOCKET || baseApiUrl;
        console.log(`Socket.IO: Tentando conectar a: ${socketUrl}`); // DEBUG
        this.socket = io(socketUrl);

        // Listener para o evento 'novaNotificacao' vindo do servidor (genérico, se ainda usar)
        this.socket.on("novaNotificacao", (data) => {
          console.log("App.vue: Recebida novaNotificacao:", data); // DEBUG
          this.showNotification(
            data.message,
            data.type || "info",
            data.duration
          );
        });

        // NOVO LISTENER: Para notificações de atualização direcionadas ao usuário
        this.socket.on("solicitacaoAtualizadaUsuario", (data) => {
          console.log("App.vue: Recebida solicitacaoAtualizadaUsuario:", data); // DEBUG
          this.showNotification(
            data.message,
            data.type || "info",
            data.duration
          );
        });

        // NOVO LISTENER: Para notificações de atualização direcionadas ao departamento (sala)
        this.socket.on("solicitacaoAtualizadaDepartamento", (data) => {
          console.log(
            "App.vue: Recebida solicitacaoAtualizadaDepartamento:",
            data
          ); // DEBUG
          this.showNotification(
            data.message,
            data.type || "warning",
            data.duration
          );
        });

        // NOVO LISTENER: Para notificações de nova solicitação para o departamento (sala)
        this.socket.on("novaSolicitacaoDepartamento", (data) => {
          console.log("App.vue: Recebida novaSolicitacaoDepartamento:", data); // DEBUG
          this.showNotification(
            data.message,
            data.type || "success",
            data.duration
          );
        });

        // NOVO LISTENER: Para notificações de sucesso na criação da solicitação para o próprio usuário
        this.socket.on("solicitacaoCriadaSucesso", (data) => {
          console.log("App.vue: Recebida solicitacaoCriadaSucesso:", data); // DEBUG
          this.showNotification(
            data.message,
            data.type || "success",
            data.duration
          );
        });

        // NOVO LISTENER: Para capturar o status de entrega da notificação (se o usuário não está online)
        this.socket.on("statusEntregaNotificacao", (data) => {
          console.log(
            "App.vue: Recebida statusEntregaNotificacao (usuário offline):",
            data
          ); // DEBUG
          this.showNotification(
            data.message,
            data.type || "warning",
            data.duration
          );
        });

        // Listeners para monitorar o status da conexão (útil para depuração)
        this.socket.on("connect", () => {
          console.log("Socket.IO: Conectado ao servidor!");
          // Após a conexão, envia o ID do funcionário para o backend para mapeamento
          if (this.user && this.user.id_funcionario) {
            this.socket.emit("setUserId", this.user.id_funcionario);
            console.log(
              `Socket.IO: Emitindo 'setUserId' para o funcionário: ${this.user.id_funcionario}`
            );
          }
        });

        this.socket.on("disconnect", () => {
          console.log("Socket.IO: Desconectado do servidor!");
        });

        this.socket.on("connect_error", (error) => {
          console.error("Socket.IO: Erro de conexão:", error);
          this.showNotification(
            "Erro ao conectar ao servidor de notificações.",
            "error"
          );
        });
      }
    },
    // Método para exibir uma notificação globalmente
    showNotification(message, type = "info", duration = 3000) {
      console.log("App.vue: Chamando showNotification com:", {
        message,
        type,
        duration,
      }); // DEBUG
      // Atualiza os dados da notificação no estado do componente
      this.notification.message = message;
      this.notification.type = type;
      this.notification.duration = duration;

      // Acessa o componente ToastNotification através da sua referência (ref="toast")
      // e chama o método 'show()' dele para exibir a notificação.
      if (this.$refs.toast) {
        console.log(
          "App.vue: Referência ao toast encontrada, chamando show()."
        ); // DEBUG
        this.$refs.toast.show();
      } else {
        console.warn(
          "App.vue: Referência ao toast não encontrada. O componente pode não estar montado."
        ); // DEBUG
      }
    },
  },

  mounted() {
    this.showNotification("🚀 Notificação de teste manual!", "info", 5000);
  },
  created() {
    this.validateToken();
  },
  beforeDestroy() {
    if (this.socket) {
      this.socket.disconnect();
      console.log("Socket.IO: Conexão desconectada em beforeDestroy.");
    }
  },
};
</script>

<style>
/* Seus estilos CSS globais permanecem aqui */
* {
  font-family: "Lato", sans-serif;
}

body {
  margin: 0;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  height: 100vh;
  display: grid;
  grid-template-rows: 60px 1fr 40px;
  grid-template-columns: 400px 1fr;
  grid-template-areas:
    "header header"
    "menu content"
    "menu footer";
}

#app.hide-menu {
  grid-template-areas:
    "header header"
    "content content"
    "footer footer";
}
</style>
